<template>
    <div id='live-timer' v-if="liveState" :class="{
      'driving': liveState.driving,
      'overtime': liveState.started && state.overtime,
      'idle': !liveState.started
      }">
      <template v-if="!state.loading">
        <div v-if="!state.break && !liveState.driving">-</div>
        <div v-else>{{ state.clock }}</div>
        <div class='text-c text-overline'>
          <span v-if="!liveState.started">Ikke startet</span>
          <span v-else-if="!liveState.driving && !state.break">Ingen pause</span>
          <span v-else-if="state.overtime">Over tid</span>
          <span v-else-if="liveState.driving">På vej</span>
          <span v-else>Pause</span>
        </div>
      </template>
      <AppLoader size="small" v-else />
      <svg viewBox="0 0 36 36" class="circular-chart">
        <path class="circle"
          :stroke-dasharray="(state.overtime ? 1 : (1 - state.ratio)) * 100 + ',100'"
          d="M18 2.0845
            a 15.9155 15.9155 0 0 1 0 31.831
            a 15.9155 15.9155 0 0 1 0 -31.831"
        />
      </svg>
    </div>
</template>

<script lang="ts">
import { defineComponent, watch, reactive, onMounted, onUnmounted, PropType } from 'vue'
import { store } from '@/store'
import { getLiveProgress } from '@/api/tripply/live'
import { RouteLiveState } from '@/api/tripply/models'

export default defineComponent({
  name: 'LiveTimer',
  props: {
    liveState: {
      type: Object as PropType<RouteLiveState>,
      required: true
    },
  },
  setup(props) {
    let timer: NodeJS.Timeout
    const state = reactive({
      loading: true,
      ratio: 1,
      overtime: false,
      break: false,
      clock: '00:00'
    })

    async function calculateTime() {
      state.loading = false
      const liveProgress = await getLiveProgress(props.liveState)
      state.break = liveProgress.hasBreak
      state.overtime = liveProgress.overtime
      state.ratio = liveProgress.ratio
      state.clock = liveProgress.clock
    }

    watch(() => props.liveState, () => {
      calculateTime()
    })

    onMounted(() => {
      timer = setInterval(() => {
        calculateTime()
      }, 1000)
    })

    onUnmounted(() => {
      clearInterval(timer)
    })

    return { state }
  }
})
</script>

<style scoped lang='scss'>
  #live-timer {
    height: 5rem;
    width: 5rem;
    flex: 5rem 0 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 100%;
    border: 4px solid #D9EAFF;
    font-size: 20px;
    font-weight: bold;
    margin-right: 1rem;
    position: relative;
    user-select: none;
    line-height: 1.2;

    &:before {
      content: "";
      position: absolute;
      top: -1.5rem;
      left: -1.5rem;
      right: -1.5rem;
      bottom: -1.5rem;
      animation: none;
      background-image: url('../../assets/themes/classic/timer_break.svg');
      background-size: 100%;
    }
    &.driving:before, &.idle:before {
      background-image: url('../../assets/themes/classic/timer_driving.svg');
    }
    &.driving:before {
      animation: spin 8s infinite linear;
    }

    .desc {
      font-size: 12px;
      letter-spacing: 1px;
      text-transform: uppercase;
      opacity: 0.5;
      text-align: center;
    }

    &.idle {
      path.circle {
        stroke: var(--color-background-input);
      }
    }
    &.driving {
      path.circle {
        stroke: var(--color-primary);
      }
    }
    &.overtime {
      path.circle {
        stroke: var(--color-error);
      }
    }
  }

  svg.circular-chart {
    position: absolute;
    width: calc(100% + 16px);
  }
  .circle {
    fill: none;
    stroke: var(--color-primary);
    stroke-width: 2;
    stroke-linecap: round;
    transition: all .3s;
  }
  
</style>
